import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  applyResult,
  fetchSnapshot,
  initialState,
  POLL_MS,
  snapshotUrl,
  type SnapshotState,
} from './snapshot';

/**
 * The polling shell around `./snapshot.ts`.
 *
 * One fetch loop for the whole app, shared through context, so the three
 * routes cannot show three different vintages of the same data — the header
 * saying "2m ago" while the table shows something else would be exactly the
 * kind of quiet dishonesty this project is supposed to be the opposite of.
 *
 * `now` ticks on its own because the age readout has to keep counting even
 * when no fetch has landed. A page that froze at "just now" for ten minutes
 * would be lying by omission.
 */

interface Ctx extends SnapshotState {
  /** Wall clock, updated every second, so age labels stay honest. */
  now: number;
}

const SnapshotContext = createContext<Ctx | null>(null);

export function SnapshotProvider({ children }: { children: ReactNode }) {
  const url = snapshotUrl();
  const [state, setState] = useState<SnapshotState>(() => initialState(url !== null));
  const [now, setNow] = useState(() => Date.now());
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!url) return;
    const ac = new AbortController();
    let cancelled = false;

    const tick = async () => {
      const result = await fetchSnapshot(url, ac.signal);
      // An abort is a teardown, not a failure; recording it would paint an
      // error on a page that is being unmounted anyway.
      if (cancelled || ac.signal.aborted) return;
      setState(applyResult(stateRef.current, result));
    };

    void tick();
    const id = setInterval(() => void tick(), POLL_MS);
    return () => {
      cancelled = true;
      ac.abort();
      clearInterval(id);
    };
  }, [url]);

  return <SnapshotContext.Provider value={{ ...state, now }}>{children}</SnapshotContext.Provider>;
}

export function useSnapshot(): Ctx {
  const ctx = useContext(SnapshotContext);
  if (!ctx) throw new Error('useSnapshot must be used inside <SnapshotProvider>');
  return ctx;
}
